import { zodResolver } from "@hookform/resolvers/zod";
import type { JSX } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { toast } from "@/shared/lib/toast";

import { useUpdateSKU } from "../api/use-update-sku";
import { editSKUSchema, type EditSKUFormValues } from "../lib/edit-sku-schema";

interface EditSKUModalProps {
  sku: string;
  currentName: string;
  onClose: () => void;
}

const FIELD_CLASS =
  "h-10 w-full rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-3 text-sm outline-none transition focus:border-[var(--brand-500)] focus-ring disabled:opacity-50";

export function EditSKUModal({ sku, currentName, onClose }: EditSKUModalProps): JSX.Element {
  const { t } = useTranslation("inventory");
  const { mutate, isPending } = useUpdateSKU();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EditSKUFormValues>({
    resolver: zodResolver(editSKUSchema),
    defaultValues: { product_name: currentName },
  });

  function onSubmit(values: EditSKUFormValues): void {
    mutate(
      { sku, product_name: values.product_name },
      {
        onSuccess: () => {
          toast.success(t("modal.edit_sku.success", { sku }));
          onClose();
        },
      },
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={t("modal.edit_sku.title")}
      data-testid="edit-sku-modal"
    >
      <button
        type="button"
        aria-label={t("modal.edit_sku.action_cancel")}
        onClick={onClose}
        className="absolute inset-0 cursor-default"
        style={{ background: "rgba(16,24,40,.28)", backdropFilter: "blur(2px)" }}
      />
      <div
        className="relative w-full max-w-[460px] rounded-2xl bg-white"
        style={{ boxShadow: "var(--sh-modal)" }}
      >
        <h2 className="px-6 pt-5 text-[17px] font-extrabold tracking-tight text-[var(--text-primary)]">
          {t("modal.edit_sku.title")}
        </h2>
        <p className="px-6 pt-1 text-[12.5px] text-[var(--text-subtle)]">{sku}</p>
        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4 px-6 pb-6 pt-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold text-[var(--text-secondary)]">
              {t("modal.edit_sku.field_name")}
            </label>
            <input
              type="text"
              data-testid="edit-sku-product-name"
              disabled={isPending}
              className={FIELD_CLASS}
              {...register("product_name")}
            />
            {errors.product_name && (
              <p
                className="mt-1 text-xs text-[var(--intent-danger-fg)]"
                data-testid="edit-sku-product-name-error"
              >
                {errors.product_name.message}
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2.5 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              data-testid="edit-sku-cancel"
              className="h-10 rounded-[var(--control-r)] border border-[var(--premium-border-strong)] px-4 text-[13.5px] font-semibold text-[var(--text-secondary)] transition hover:bg-[var(--canvas)] disabled:opacity-50"
            >
              {t("modal.edit_sku.action_cancel")}
            </button>
            <button
              type="submit"
              disabled={isPending}
              data-testid="edit-sku-submit"
              className="inline-flex h-10 items-center gap-1.5 rounded-[var(--control-r)] px-5 text-[13.5px] font-bold text-white transition hover:-translate-y-px disabled:opacity-50"
              style={{
                background: "var(--grad-primary-btn)",
                boxShadow:
                  "0 1px 2px rgba(15,98,254,.5),0 6px 16px -4px rgba(15,98,254,.5),inset 0 1px 0 rgba(255,255,255,.22)",
              }}
            >
              {isPending ? t("modal.edit_sku.action_saving") : t("modal.edit_sku.action_save")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
